/**
 * Standardized Chart Colors for Business Performance Reports
 *
 * This color palette ensures consistency across all Recharts visualizations
 * and provides semantic meaning to different metric types.
 */

export const CHART_COLORS = {
    // Primary metrics
    PRIMARY_BLUE: '#2563eb', // Primary Blue - Doanh thu thuần
    AMBER_ORANGE: '#f59e0b', // Amber Orange - Doanh thu dự kiến/Forecast
    EMERALD_GREEN: '#10b981', // Emerald Green - Thu nhập khác/Lợi nhuận dương
    ROSE_RED: '#ef4444', // Rose Red - Lợi nhuận gộp/Chi phí/Lỗ
    INDIGO_PURPLE: '#8b5cf6', // Indigo Purple - Lợi nhuận từ HĐKD
    PINK_FUCHSIA: '#ec4899', // Pink Fuchsia - Lợi nhuận trước thuế
    SLATE_GRAY: '#64748b', // Slate Gray - Lợi nhuận sau thuế
    SKY_CYAN: '#06b6d4', // Sky Cyan - Tăng trưởng/chỉ số phụ
    LIME_GREEN: '#84cc16', // Lime Green - Chỉ số hiệu suất (ROE, ROI)
    WARM_BROWN: '#b45309', // Warm Brown - Chi phí khác/khoản giảm trừ
    TEAL_BLUE: '#14b8a6', // Teal Blue - Financial ratios
    ORANGE_RED: '#f97316', // Orange Red - Financial ratios
    VIOLET_PURPLE: '#a855f7', // Violet Purple - Financial ratios
    BLUE_GRAY: '#475569', // Blue Gray - Financial ratios
} as const

/**
 * Metric-to-Color Mapping
 * Maps business metrics to their designated colors for consistency
 */
export const METRIC_COLORS = {
    // Revenue metrics
    net_revenue: CHART_COLORS.PRIMARY_BLUE,
    projected_revenue: CHART_COLORS.AMBER_ORANGE,
    net_other_income: CHART_COLORS.EMERALD_GREEN,

    // Profit metrics
    gross_profit: CHART_COLORS.ROSE_RED,
    operating_profit: CHART_COLORS.INDIGO_PURPLE,
    profit_before_tax: CHART_COLORS.PINK_FUCHSIA,
    net_profit: CHART_COLORS.SLATE_GRAY,

    // Cost metrics
    cost_of_goods_sold: CHART_COLORS.WARM_BROWN,
    operating_expenses: CHART_COLORS.WARM_BROWN,
    other_expenses: CHART_COLORS.WARM_BROWN,

    // Performance indicators
    gross_margin: CHART_COLORS.SKY_CYAN,
    operating_margin: CHART_COLORS.SKY_CYAN,
    net_margin: CHART_COLORS.SKY_CYAN,
    roi: CHART_COLORS.LIME_GREEN,

    // Growth metrics
    revenue_growth: CHART_COLORS.SKY_CYAN,
    profit_growth: CHART_COLORS.SKY_CYAN,

    // Financial Strength Ratios
    current_ratio: CHART_COLORS.PRIMARY_BLUE,
    quick_ratio: CHART_COLORS.TEAL_BLUE,
    interest_coverage: CHART_COLORS.EMERALD_GREEN,
    debt_to_equity_ratio: CHART_COLORS.ROSE_RED,

    // Management Efficiency Ratios
    roa: CHART_COLORS.LIME_GREEN,
    roe: CHART_COLORS.INDIGO_PURPLE,
    roic: CHART_COLORS.VIOLET_PURPLE,
    roce: CHART_COLORS.PINK_FUCHSIA,

    // Operating Capability Ratios
    total_asset_turnover: CHART_COLORS.AMBER_ORANGE,
    inventory_turnover: CHART_COLORS.SKY_CYAN,
    receivables_turnover: CHART_COLORS.EMERALD_GREEN,
    current_asset_turnover: CHART_COLORS.INDIGO_PURPLE,

    // Balance Sheet
    total_assets: CHART_COLORS.PRIMARY_BLUE,
    total_liability: CHART_COLORS.ROSE_RED,
    total_equity: CHART_COLORS.EMERALD_GREEN,
} as const

/**
 * Color Palette Array
 * For charts that need multiple colors in sequence
 */
export const CHART_COLOR_PALETTE = [
    CHART_COLORS.PRIMARY_BLUE,
    CHART_COLORS.AMBER_ORANGE,
    CHART_COLORS.EMERALD_GREEN,
    CHART_COLORS.ROSE_RED,
    CHART_COLORS.INDIGO_PURPLE,
    CHART_COLORS.PINK_FUCHSIA,
    CHART_COLORS.SLATE_GRAY,
    CHART_COLORS.SKY_CYAN,
    CHART_COLORS.LIME_GREEN,
    CHART_COLORS.WARM_BROWN,
] as const

/**
 * Get color for a specific metric
 * Falls back to palette index if metric not found
 */
export const getMetricColor = (
    metricKey: string,
    fallbackIndex: number = 0
): string => {
    return (
        METRIC_COLORS[metricKey as keyof typeof METRIC_COLORS] ||
        CHART_COLOR_PALETTE[fallbackIndex % CHART_COLOR_PALETTE.length]
    )
}

/**
 * Chart Theme Configuration
 * Common styling for all Recharts components
 */
export const CHART_THEME = {
    // Grid
    grid: {
        stroke: '#e5e7eb',
        strokeDasharray: '3 3',
    },

    // Axes
    axis: {
        stroke: '#9ca3af',
        fontSize: 12,
        fontFamily: 'inherit',
    },

    // Tooltip
    tooltip: {
        contentStyle: {
            backgroundColor: '#ffffff',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            padding: '12px',
        },
        itemStyle: {
            color: '#374151',
            fontSize: '13px',
        },
        labelStyle: {
            color: '#111827',
            fontWeight: 600,
            marginBottom: '8px',
        },
    },

    // Legend
    legend: {
        fontSize: 14,
        fontFamily: 'inherit',
        iconType: 'circle' as const,
    },
} as const

/**
 * Helper function to get chart colors with opacity
 */
export const getColorWithOpacity = (color: string, opacity: number): string => {
    // Convert hex to rgba
    const r = parseInt(color.slice(1, 3), 16)
    const g = parseInt(color.slice(3, 5), 16)
    const b = parseInt(color.slice(5, 7), 16)
    return `rgba(${r}, ${g}, ${b}, ${opacity})`
}

/**
 * Semantic color groups for quick access
 */
export const SEMANTIC_COLORS = {
    revenue: [
        CHART_COLORS.PRIMARY_BLUE,
        CHART_COLORS.AMBER_ORANGE,
        CHART_COLORS.EMERALD_GREEN,
    ],
    profit: [
        CHART_COLORS.ROSE_RED,
        CHART_COLORS.INDIGO_PURPLE,
        CHART_COLORS.PINK_FUCHSIA,
        CHART_COLORS.SLATE_GRAY,
    ],
    performance: [CHART_COLORS.SKY_CYAN, CHART_COLORS.LIME_GREEN],
    costs: [CHART_COLORS.WARM_BROWN, CHART_COLORS.ROSE_RED],
} as const
